import { httpClient } from './http-client';
import type { ApiResponse } from '../types/api';

/**
 * Investments Service
 * Handles investment-related API calls including ACB calculations and holdings
 */

export interface ACBPosition {
  symbol: string;
  total_shares: string;
  total_cost_basis: string;
  acb_per_share: string;
}

interface ACBCalculation {
  account_id: string;
  symbol: string;
  total_shares: string;
  total_cost_basis: string;
  acb_per_share: string;
  market_value: string;
  unrealized_gains: string;
  /**
   * False when no quote could be obtained. Check this before showing
   * market_value or unrealized_gains: zero means UNKNOWN, not worthless.
   */
  market_value_available: boolean;
  price_as_of?: string;
  price_currency?: string;
  lots: InvestmentLot[];
  calculated_at: string;
}

/**
 * A recorded trade. This is the append-only history the ACB calculation is
 * replayed from, and since migration 054 it is where the per-purchase detail
 * lives: `/lots` now returns the POOLED position (one row per symbol at one
 * average cost), which is what CRA says a holding of identical property is.
 */
export interface InvestmentTrade {
  id: string;
  account_id: string;
  symbol: string;
  trade_date: string;
  transaction_type: string;
  shares: string;
  price: string;
  fees: string;
  currency: string;
  fx_rate: string;
  notes?: string | null;
}

export interface InvestmentLot {
  id: string;
  symbol: string;
  shares: string;
  cost_basis: string;
  cost_per_share: string;
  acquired_date: string;
  transaction_type: string;
  notes?: string | null;
}

interface CreateInvestmentTransactionRequest {
  account_id: string;
  symbol: string;
  shares: string;
  price: string;
  transaction_type: 'BUY' | 'SELL' | 'REINVEST' | 'TRANSFER_IN' | 'TRANSFER_OUT';
  date: string;
  currency: string;
  /** Commission and fees in `currency`. Omitted means zero. */
  fees?: string;
  /**
   * Rate converting `currency` to CAD ON `date` -- not today. Omitted means 1,
   * which is correct for a CAD trade. Stored with the trade so a report run
   * next year reproduces this year's number.
   */
  fx_rate?: string;
  notes?: string;
}

interface TransactionResponse {
  transaction_id: string;
  success: boolean;
  message: string;
  created_lots: number;
  updated_lots: number;
  acb_calculation: ACBCalculation;
  capital_gain?: string;
  acb_used?: string;
  /** Portion of a loss denied as superficial and added back to the ACB. */
  denied_loss?: string;
  superficial?: boolean;
}

interface ACBSummary {
  account_id: string;
  positions: ACBPosition[];
  total_acb: string;
  position_count: number;
}

export interface ConsolidatedHolding {
  symbol: string;
  total_shares: string;
  total_cost_basis: string;
  acb_per_share: string;
  account_count: number;
  account_names: string[];
  account_ids: string[];
}

class InvestmentsService {
  private static readonly BASE_URL = '/investments';

  /**
   * Create a new investment transaction (buy/sell)
   */
  static async createTransaction(
    data: CreateInvestmentTransactionRequest
  ): Promise<ApiResponse<TransactionResponse>> {
    return httpClient.post(`${InvestmentsService.BASE_URL}/transactions`, data);
  }

  /**
   * Get ACB calculation for a specific symbol in an account
   */
  static async getACBCalculation(
    accountId: string,
    symbol: string
  ): Promise<ApiResponse<ACBCalculation>> {
    return httpClient.get(
      `${InvestmentsService.BASE_URL}/accounts/${accountId}/acb/${symbol}`
    );
  }

  /**
   * Get ACB summary for all holdings in an account
   */
  static async getACBSummary(
    accountId: string
  ): Promise<ApiResponse<ACBSummary>> {
    return httpClient.get(
      `${InvestmentsService.BASE_URL}/accounts/${accountId}/acb`
    );
  }

  /**
   * Get investment lots for an account, optionally filtered by symbol
   */
  static async getInvestmentLots(
    accountId: string,
    symbol?: string
  ): Promise<ApiResponse<{ account_id: string; symbol?: string; lots: InvestmentLot[]; count: number }>> {
    const url = symbol
      ? `${InvestmentsService.BASE_URL}/accounts/${accountId}/lots?symbol=${symbol}`
      : `${InvestmentsService.BASE_URL}/accounts/${accountId}/lots`;
    return httpClient.get(url);
  }

  /**
   * Get the append-only trade history for an account, optionally by symbol.
   */
  static async getInvestmentTrades(
    accountId: string,
    symbol?: string
  ): Promise<ApiResponse<{ account_id: string; symbol?: string; trades: InvestmentTrade[]; count: number }>> {
    const url = symbol
      ? `${InvestmentsService.BASE_URL}/accounts/${accountId}/trades?symbol=${symbol}`
      : `${InvestmentsService.BASE_URL}/accounts/${accountId}/trades`;
    return httpClient.get(url);
  }

  /**
   * Get consolidated holdings across all investment accounts
   */
  static async getConsolidatedHoldings(): Promise<ApiResponse<{ holdings: ConsolidatedHolding[]; count: number }>> {
    const rawResponse = await httpClient.get<any>(`${InvestmentsService.BASE_URL}/consolidated`);
    
    // Backend returns: { holdings: [], count: number }
    // Handle both wrapped and unwrapped responses
    if (!rawResponse) {
      return {
        data: {
          holdings: [],
          count: 0,
        },
      };
    }
    
    const responseData = (rawResponse as any).data || rawResponse;
    
    // Wrap it to match the expected ApiResponse format
    return {
      data: {
        holdings: responseData.holdings || [],
        count: responseData.count || 0,
      },
    };
  }
}

// Export singleton instance
export const investmentsService = InvestmentsService;
